import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface User {
  id: number;
  name: string;
  role: string;
}

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  rate: number;
}

export interface CurrencyInfo {
  country_code: string;
  country_name: string;
  currency_code: string;
  currency_symbol: string;
  currency_name: string;
  flag_emoji: string;
  exchange_rate: number;
  is_auto_detected: boolean;
}

export const CURRENCIES: Currency[] = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', rate: 1650 },
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.36 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.53 },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', rate: 18.5 },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', rate: 129 },
  { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi', rate: 15.8 },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83 },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67 },
];

export const COUNTRY_FLAGS: { [key: string]: string } = {
  'NG': '🇳🇬',
  'US': '🇺🇸',
  'GB': '🇬🇧',
  'CA': '🇨🇦',
  'AU': '🇦🇺',
  'ZA': '🇿🇦',
  'KE': '🇰🇪',
  'GH': '🇬🇭',
  'DE': '🇩🇪',
  'FR': '🇫🇷',
  'IN': '🇮🇳',
  'AE': '🇦🇪',
  'SA': '🇸🇦',
  'EG': '🇪🇬',
  'JP': '🇯🇵',
  'CN': '🇨🇳',
  'SG': '🇸🇬',
  'MY': '🇲🇾',
  'BR': '🇧🇷',
  'MX': '🇲🇽',
};

interface CurrencyContextType {
  currency: Currency;
  currencyInfo: CurrencyInfo | null;
  setCurrency: (currency: Currency) => void;
  updateCurrencyPreference: (currencyCode: string) => Promise<void>;
  resetToAutoDetected: () => Promise<void>;
  convertPrice: (amountInNGN: number) => number;
  formatPrice: (amountInNGN: number) => string;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const DEFAULT_CURRENCY_INFO: CurrencyInfo = {
  country_code: 'NG',
  country_name: 'Nigeria',
  currency_code: 'NGN',
  currency_symbol: '₦',
  currency_name: 'Nigerian Naira',
  flag_emoji: '🇳🇬',
  exchange_rate: 1650,
  is_auto_detected: true
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [loading, setLoading] = useState(false);
  const [currencyInfo, setCurrencyInfo] = useState<CurrencyInfo | null>(() => {
    const saved = localStorage.getItem('currencyInfo');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_CURRENCY_INFO;
      }
    }
    return DEFAULT_CURRENCY_INFO;
  });

  const [currency, setCurrencyState] = useState<Currency>(() => {
    if (currencyInfo) {
      const found = CURRENCIES.find(c => c.code === currencyInfo.currency_code);
      if (found) return found;
    }
    return CURRENCIES[0];
  });

  const fetchCurrencyInfo = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/user/currency', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data: CurrencyInfo = await response.json();
        setCurrencyInfo(data);
        localStorage.setItem('currencyInfo', JSON.stringify(data));
        
        const matchingCurrency = CURRENCIES.find(c => c.code === data.currency_code);
        if (matchingCurrency) {
          setCurrencyState({
            ...matchingCurrency,
            rate: data.exchange_rate
          });
        }
      }
    } catch (error) {
      console.error('Error fetching currency info:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCurrencyInfo();
    }
  }, [isAuthenticated, user, fetchCurrencyInfo]);

  useEffect(() => {
    localStorage.setItem('selectedCurrency', JSON.stringify(currency));
  }, [currency]);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
  };

  const updateCurrencyPreference = async (currencyCode: string) => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/user/currency', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currency_code: currencyCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCurrencyInfo: CurrencyInfo = {
          country_code: data.country_code,
          country_name: data.country_name,
          currency_code: data.currency_code,
          currency_symbol: data.currency_symbol,
          currency_name: data.currency_name,
          flag_emoji: data.flag_emoji,
          exchange_rate: data.exchange_rate,
          is_auto_detected: data.is_auto_detected
        };
        
        setCurrencyInfo(newCurrencyInfo);
        localStorage.setItem('currencyInfo', JSON.stringify(newCurrencyInfo));
        
        const matchingCurrency = CURRENCIES.find(c => c.code === data.currency_code);
        if (matchingCurrency) {
          setCurrencyState({
            ...matchingCurrency,
            rate: data.exchange_rate
          });
        }
      }
    } catch (error) {
      console.error('Error updating currency preference:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetToAutoDetected = async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/user/currency/reset', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCurrencyInfo: CurrencyInfo = {
          country_code: data.country_code,
          country_name: data.country_name,
          currency_code: data.currency_code,
          currency_symbol: data.currency_symbol,
          currency_name: data.currency_name,
          flag_emoji: data.flag_emoji,
          exchange_rate: data.exchange_rate,
          is_auto_detected: data.is_auto_detected
        };
        
        setCurrencyInfo(newCurrencyInfo);
        localStorage.setItem('currencyInfo', JSON.stringify(newCurrencyInfo));
        
        const matchingCurrency = CURRENCIES.find(c => c.code === data.currency_code);
        if (matchingCurrency) {
          setCurrencyState({
            ...matchingCurrency,
            rate: data.exchange_rate
          });
        }
      }
    } catch (error) {
      console.error('Error resetting currency:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertPrice = (amountInNGN: number): number => {
    if (currency.code === 'NGN') {
      return amountInNGN;
    }
    return (amountInNGN / 1650) * currency.rate;
  };

  const formatPrice = (amountInNGN: number): string => {
    const converted = convertPrice(amountInNGN);
    return `${currency.symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      currencyInfo,
      setCurrency, 
      updateCurrencyPreference,
      resetToAutoDetected,
      convertPrice, 
      formatPrice,
      loading
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
